import { useState, useEffect, memo } from "react";
import axios from "axios";
import { API } from "@/App";
import MiniSparkline from "@/components/market/MiniSparkline";
import { Loader2, AlertCircle } from "lucide-react";
import { fmt } from "./utils";

const AllIndicesTable = memo(({ navigate }) => {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    // AbortController for timeout — prevent infinite loading
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 25000); // 25s client-side timeout

    axios.get(`${API}/indices/all`, { signal: controller.signal })
      .then(r => {
        setAllData(r.data.indices || []);
        setError(false);
      })
      .catch((err) => {
        if (axios.isCancel(err) || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
          setError(true); // Timeout
        } else {
          setError(true);
        }
      })
      .finally(() => {
        clearTimeout(timeout);
        setLoading(false);
      });

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, []);

  if (loading) return (
    <div data-testid="all-indices-table">
      <h2 className="text-lg font-bold text-gray-900 mb-4">All Indices</h2>
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin w-6 h-6 text-gray-400 mr-2" />
        <span className="text-sm text-gray-500">Loading all indices...</span>
      </div>
    </div>
  );

  if (error || !allData.length) return (
    <div data-testid="all-indices-table">
      <h2 className="text-lg font-bold text-gray-900 mb-4">All Indices</h2>
      <div className="flex flex-col items-center justify-center py-8 gap-2">
        <AlertCircle className="w-5 h-5 text-gray-400" />
        <p className="text-sm text-gray-400">
          {error ? "Unable to load indices data. Please try again later." : "No indices data available"}
        </p>
      </div>
    </div>
  );

  return (
    <div data-testid="all-indices-table">
      <h2 className="text-lg font-bold text-gray-900 mb-4">All Indices</h2>
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-3">Index Name</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-3 w-24"></th>
              <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-3">Market Value</th>
              <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-3">52W High</th>
              <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-3">52W Low</th>
            </tr>
          </thead>
          <tbody>
            {allData.map((idx) => {
              const price = idx.last_price;
              const chg = idx.change || 0;
              const chgPct = idx.change_percent || 0;
              const isPos = chgPct >= 0;
              const sparkData = idx.sparkline || [];
              const w52High = idx.week_52?.high;
              const w52Low = idx.week_52?.low;
              const hasPriceData = price != null && price > 0;
              return (
                <tr
                  key={idx.slug}
                  className="border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/indices/${idx.slug}`)}
                  data-testid={`index-row-${idx.slug}`}
                >
                  <td className="py-3.5 px-3 text-sm font-semibold text-gray-900">{idx.name}</td>
                  <td className="py-3.5 px-3">
                    {sparkData.length >= 2 && (
                      <MiniSparkline prices={sparkData} color={isPos ? "#00b386" : "#eb5b3c"} width={80} height={28} />
                    )}
                  </td>
                  <td className="py-3.5 px-3 text-right">
                    {hasPriceData ? (
                      <>
                        <p className="text-sm font-semibold text-gray-900">{fmt(price)}</p>
                        <p className={`text-xs ${isPos ? "text-[#00b386]" : "text-[#eb5b3c]"}`}>
                          {chg ? `${chg >= 0 ? "+" : ""}${chg.toFixed(2)} ` : ""}
                          ({Math.abs(chgPct).toFixed(2)}%)
                        </p>
                      </>
                    ) : (
                      <p className="text-xs text-gray-400">Unavailable</p>
                    )}
                  </td>
                  <td className="py-3.5 px-3 text-right text-sm text-gray-700">{w52High ? fmt(w52High) : "--"}</td>
                  <td className="py-3.5 px-3 text-right text-sm text-gray-700">{w52Low ? fmt(w52Low) : "--"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default AllIndicesTable;
